import React from "react";
import { toast } from "react-toastify";

import { KintoneExportedRecord } from "@graffer-inc/cloud-gov-core";
import {
  DangerSecondaryButton,
  ErrorDialog,
  Pagination,
  Table,
  TableBody,
  TableHeader,
  TableRow,
  Td,
  Th,
} from "@graffer-inc/console-design-kit";

import type { DeleteKintoneExportedRecordFuncType } from "../../../../pages/data-transfer/kintone-export/page-hooks/kintone-export-setting-detail/hook";
import { unixSecToTimeString } from "../../../../../utils/date-utils";

type Props = {
  exportedRecords: KintoneExportedRecord[];

  onClickDeleteExportedRecord: DeleteKintoneExportedRecordFuncType;

  currentExportedRecordsPage: number;
  setCurrentExportedRecordsPage: (page: number) => void;
  lastExportedRecordsPage: number;
};

export const KintoneExportedRecordsList: React.FC<Props> = ({
  exportedRecords,
  onClickDeleteExportedRecord,
  currentExportedRecordsPage,
  setCurrentExportedRecordsPage,
  lastExportedRecordsPage,
}) => {
  const [deleteTargetId, setDeleteTargetId] =
    React.useState<KintoneExportedRecord["id"]>();
  const [isDeleting, setDeleting] = React.useState(false);

  return (
    <section>
      <Table>
        <TableHeader>
          <TableRow>
            <Th>ID</Th>
            <Th>申請 ID</Th>
            <Th>Kintone レコード ID</Th>
            <Th>連携日時</Th>
            <Th></Th>
          </TableRow>
        </TableHeader>
        <TableBody>
          {exportedRecords.map(
            ({
              id,
              smartApplySubmissionId,
              kintoneRecordId,
              createdAtUnixSecond,
            }) => {
              return (
                <TableRow key={id}>
                  <Td>{id}</Td>
                  <Td>{smartApplySubmissionId}</Td>
                  <Td>{kintoneRecordId} </Td>
                  <Td>{unixSecToTimeString(createdAtUnixSecond)}</Td>
                  <Td>
                    <DangerSecondaryButton
                      text="削除"
                      onClick={() => {
                        setDeleteTargetId(id);
                      }}
                    />
                  </Td>
                </TableRow>
              );
            },
          )}
        </TableBody>
      </Table>
      <Pagination
        onPageClick={setCurrentExportedRecordsPage}
        currentPage={currentExportedRecordsPage}
        lastPage={lastExportedRecordsPage}
      />
      {typeof deleteTargetId === "string" && (
        <ErrorDialog
          title="ひも付きを削除しようとしています"
          onCancel={() => {
            setDeleteTargetId(undefined);
          }}
          onProceed={() => {
            if (isDeleting) {
              return;
            }
            setDeleting(true);
            onClickDeleteExportedRecord(
              { id: deleteTargetId },
              {
                onSucceeded: () => {
                  toast.success("削除しました");
                  setDeleteTargetId(undefined);
                  setDeleting(false);
                },
                onFailed: (errorMessage) => {
                  toast.error(`エラーが発生しました: ${errorMessage}`);
                  setDeleteTargetId(undefined);
                },
              },
            );
          }}
          cancelText="キャンセル"
          proceedText="削除"
          contents={<DeleteDialogContent />}
        />
      )}
    </section>
  );
};

const DeleteDialogContent: React.FC = () => {
  return (
    <div>
      <p>申請 id とレコード id とのひも付きを削除しようとしています。</p>
      <p>この操作は元に戻すことができません。</p>
      <p>
        削除されるのはグラファーのサーバーで保存されている「連携のひも付き」のみです。
        Kintone のレコードを削除する必要がある場合は Kintone
        の画面からレコードを削除してください。
      </p>
    </div>
  );
};
